import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { DeleteButton } from '../../components/ActionButtons';
import DataTable from '../../components/DataTable';
import { apiRequest } from '../../services/api';
import DatePicker from '../../components/DatePicker';
import UsageInfo from '../../components/UsageInfo';
import { useKasa } from '../../context/KasaContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import PageHeader from '../../components/PageHeader';

const GUNLER = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];
const isoToDP = (iso) => { if (!iso) return ''; const [y, m, d] = iso.split('-'); return `${d}.${m}.${y}`; };
const dpToIso = (dp) => { if (!dp) return ''; const [d, m, y] = dp.split('.'); return `${y}-${m}-${d}`; };
const baslangicBitistenSonra = (bas, bit) => dpToIso(bas) > dpToIso(bit);
const fmt = (n, pb) => (n || n === 0) ? Number(n).toLocaleString('tr-TR') + ' ' + (pb || 'TL') : '—';
const tarihGoster = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const gunler = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const aylar = ['', 'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
  return `${d.getDate()} ${aylar[d.getMonth() + 1]} ${d.getFullYear()} ${gunler[d.getDay()]}`;
};
const bosGunlukFiyat = () => Object.fromEntries(GUNLER.map(g => [g, 0]));
const bosKisiBasiFiyat = (max) => Object.fromEntries(Array.from({ length: max }, (_, i) => [`${i + 1}.Kişi`, 0]));
const fiyatToTL = (fiyat, paraBirimi, kurlari) => {
  if (!fiyat || !paraBirimi || paraBirimi === 'TL' || paraBirimi === 'TRY') return null;
  const kur = kurlari?.[paraBirimi] || 1;
  return fiyat * kur;
};

function apidanState(dto, max) {
  const g = bosGunlukFiyat();
  dto.gunlukFiyatlar?.forEach(f => { if (g.hasOwnProperty(f.gun)) g[f.gun] = Number(f.fiyat); });
  const k = {};
  dto.kisiBasiFiyatlar?.forEach(f => { k[`${f.kisiSayisi}.Kişi`] = Number(f.fiyat); });
  for (let i = 1; i <= max; i++) if (!k.hasOwnProperty(`${i}.Kişi`)) k[`${i}.Kişi`] = 0;
  return {
    odaTipiId: dto.odaTipiId,
    kisiBasiAktif: dto.kisiBasiAktif,
    gunlukParaBirimi: dto.gunlukFiyatlar?.[0]?.paraBirimi || 'TRY',
    kisiBasiParaBirimi: dto.kisiBasiFiyatlar?.[0]?.paraBirimi || 'TRY',
    gunlukFiyat: g,
    kisiBasiFiyat: k,
    ozelFiyatlar: (dto.ozelFiyatlar || []).map(f => ({ id: f.odaTipiOzelFiyatId, bas: f.baslangicTarihi?.slice(0, 10), bit: f.bitisTarihi?.slice(0, 10), fiyat: Number(f.fiyat), paraBirimi: f.paraBirimi || 'TRY' }))
  };
}

const bosKural = () => ({ ad: '', minYas: 0, maxYas: 12, fiyatTipi: 'UCRETSIZ', deger: 0, paraBirimi: 'TRY', aktifMi: true });

export default function OdaFiyatlandirma() {
  const { paraBirimleri, dovizKurlari } = useKasa();
  const [odaTipleri, setOdaTipleri] = useState([]);
  const [seciliId, setSeciliId] = useState(null);
  const [fiyatState, setFiyatState] = useState(null);
  const [sekme, setSekme] = useState('standart');
  const [yukleniyor, setYukleniyor] = useState(true);
  const [fiyatYukleniyor, setFiyatYukleniyor] = useState(false);
  const [kaydediliyor, setKaydediliyor] = useState(false);
  const [mesaj, setMesaj] = useState(null);
  const [resKaydet, setResKaydet] = useState(false);
  const [yuzdeFark, setYuzdeFark] = useState('2');
  const [yeniOzel, setYeniOzel] = useState({ bas: '', bit: '', fiyat: '', paraBirimi: 'TRY' });
  const [ozelEkleniyor, setOzelEkleniyor] = useState(false);
  const [tarihHata, setTarihHata] = useState('');
  const [maxKisiDuzenle, setMaxKisiDuzenle] = useState(null);
  const [cocukKurallari, setCocukKurallari] = useState([]);
  const [kuralForm, setKuralForm] = useState(false);
  const [yeniKural, setYeniKural] = useState(bosKural());

  const toTitleCase = (str) => {
    return str.toLocaleLowerCase('tr-TR').split(' ').map(word => 
      word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1)
    ).join(' ');
  };

  const fetchTipler = useCallback(async () => {
    setYukleniyor(true);
    try {
      const data = await apiRequest('OdaTipi');
      const tipler = (data || []).filter(o => o.aktifMi !== false);
      setOdaTipleri(tipler);
      if (tipler.length > 0) setSeciliId(tipler[0].odaTipiId);
    } catch { setMesaj({ tip: 'HATA', metin: 'Oda tipleri yüklenemedi.' }); }
    setYukleniyor(false);
  }, []);

  const fetchFiyatlar = useCallback(async (id) => {
    if (!id) return;
    setFiyatYukleniyor(true);
    const tip = odaTipleri.find(o => o.odaTipiId === id);
    const max = tip?.maxKisiSayisi || 2;
    const normPb = (pb, valueMap) => {
      if (!paraBirimleri.length || paraBirimleri.some(p => p.tcmbKod === pb)) return { pb, valueMap };
      const hedef = paraBirimleri.find(p => p.tcmbKod === 'TRY') || paraBirimleri[0];
      if (!hedef) return { pb, valueMap };
      const getKur = (p) => (p && p !== 'TL' && p !== 'TRY' && dovizKurlari[p] > 0) ? dovizKurlari[p] : 1;
      const eskiKur = getKur(pb); const yeniKur = getKur(hedef.tcmbKod);
      const d = (f) => eskiKur === yeniKur ? f : Math.round((f || 0) * eskiKur / yeniKur * 100) / 100;
      return { pb: hedef.tcmbKod, valueMap: Object.fromEntries(Object.entries(valueMap).map(([k, f]) => [k, d(f)])) };
    };
    try {
      const data = await apiRequest(`odafiyat/${id}`);
      const state = apidanState(data, max);
      const g = normPb(state.gunlukParaBirimi, state.gunlukFiyat);
      const kb = normPb(state.kisiBasiParaBirimi, state.kisiBasiFiyat);
      state.gunlukParaBirimi = g.pb; state.gunlukFiyat = g.valueMap;
      state.kisiBasiParaBirimi = kb.pb; state.kisiBasiFiyat = kb.valueMap;
      setFiyatState(state);
    } catch {
      const ilkPb = paraBirimleri[0]?.tcmbKod || 'TRY';
      setFiyatState({ odaTipiId: id, kisiBasiAktif: false, gunlukParaBirimi: ilkPb, kisiBasiParaBirimi: ilkPb, gunlukFiyat: bosGunlukFiyat(), kisiBasiFiyat: bosKisiBasiFiyat(max), ozelFiyatlar: [] });
    }
    setFiyatYukleniyor(false);
  }, [odaTipleri, paraBirimleri, dovizKurlari]);

  const fetchCocuk = useCallback(async () => {
    try { const data = await apiRequest('cocukfiyatkurallari'); setCocukKurallari(data || []); }
    catch { setCocukKurallari([]); }
  }, []);

  useEffect(() => { fetchTipler(); fetchCocuk(); }, [fetchTipler, fetchCocuk]);
  useEffect(() => { fetchFiyatlar(seciliId); }, [seciliId, fetchFiyatlar]);

  const setF = (patch) => setFiyatState(p => ({ ...p, ...patch }));
  const aktifTip = useMemo(() => odaTipleri.find(o => o.odaTipiId === seciliId), [odaTipleri, seciliId]);

  const getKur = (pb) => (pb && pb !== 'TL' && pb !== 'TRY' && dovizKurlari[pb] > 0) ? dovizKurlari[pb] : 1;
  const donusturMap = (map, eskiPb, yeniPb) => {
    const ek = getKur(eskiPb); const yk = getKur(yeniPb);
    if (ek === yk) return map;
    return Object.fromEntries(Object.entries(map).map(([k, f]) => [k, Math.round((f || 0) * ek / yk * 100) / 100]));
  };
  const handleGunlukPbDegistir = (yeniPb) => {
    if (!fiyatState || fiyatState.gunlukParaBirimi === yeniPb) return;
    setFiyatState(p => ({ ...p, gunlukParaBirimi: yeniPb, gunlukFiyat: donusturMap(p.gunlukFiyat, p.gunlukParaBirimi, yeniPb) }));
  };
  const handleKisiBasiPbDegistir = (yeniPb) => {
    if (!fiyatState || fiyatState.kisiBasiParaBirimi === yeniPb) return;
    setFiyatState(p => ({ ...p, kisiBasiParaBirimi: yeniPb, kisiBasiFiyat: donusturMap(p.kisiBasiFiyat, p.kisiBasiParaBirimi, yeniPb) }));
  };

  const handleKaydet = async () => {
    if (!fiyatState) return;
    setKaydediliyor(true);
    try {
      const max = aktifTip?.maxKisiSayisi || 2;
      await apiRequest('odafiyat', 'PUT', { 
        odaTipiId: fiyatState.odaTipiId, 
        kisiBasiAktif: fiyatState.kisiBasiAktif, 
        gunlukFiyatlar: GUNLER.map(g => ({ gun: g, fiyat: fiyatState.gunlukFiyat[g] || 0, paraBirimi: fiyatState.gunlukParaBirimi || 'TRY' })),
        kisiBasiFiyatlar: Array.from({ length: max }, (_, i) => ({ kisiSayisi: i + 1, fiyat: fiyatState.kisiBasiFiyat[`${i + 1}.Kişi`] || 0, paraBirimi: fiyatState.kisiBasiParaBirimi || 'TRY' })) 
      });
      setMesaj({ tip: 'OK', metin: 'Fiyatlar başarıyla güncellendi.' });
      setTimeout(() => setMesaj(null), 3000);
    } catch { setMesaj({ tip: 'HATA', metin: 'Kayıt sırasında hata oluştu.' }); }
    setKaydediliyor(false);
  };

  const handleMaxKisiGuncelle = async (e, tip, delta) => {
    if (e) e.stopPropagation();
    const yeniMax = Math.max(1, Math.min(10, (tip.maxKisiSayisi || 2) + delta));
    if (yeniMax === tip.maxKisiSayisi) return;
    try {
      // Body içinde göndermeyi deneyelim, bazı backend'ler PATCH'te bunu tercih eder
      await apiRequest(`OdaTipi/${tip.odaTipiId}/maxkisi`, 'PATCH', { maxKisi: yeniMax });
      setOdaTipleri(p => p.map(o => o.odaTipiId === tip.odaTipiId ? { ...o, maxKisiSayisi: yeniMax } : o));
      if (seciliId === tip.odaTipiId) fetchFiyatlar(tip.odaTipiId);
    } catch {
      try {
        // Eğer PATCH/body başarısız olursa query param deneyelim (mevcut halinin yedeği)
        await apiRequest(`OdaTipi/${tip.odaTipiId}/maxkisi?maxKisi=${yeniMax}`, 'PATCH');
        setOdaTipleri(p => p.map(o => o.odaTipiId === tip.odaTipiId ? { ...o, maxKisiSayisi: yeniMax } : o));
        if (seciliId === tip.odaTipiId) fetchFiyatlar(tip.odaTipiId);
      } catch {
        setMesaj({ tip: 'HATA', metin: 'Kişi kapasitesi güncellenemedi.' });
        setTimeout(() => setMesaj(null), 3000);
      }
    }
  };

  const handleOzelEkle = async () => {
    if (!yeniOzel.bas || !yeniOzel.bit || !yeniOzel.fiyat) return;
    if (baslangicBitistenSonra(yeniOzel.bas, yeniOzel.bit)) { setTarihHata('Başlangıç bitişten sonra olamaz.'); return; }
    setOzelEkleniyor(true);
    try {
      const r = await apiRequest('odafiyat/ozel', 'POST', { odaTipiId: seciliId, baslangicTarihi: dpToIso(yeniOzel.bas), bitisTarihi: dpToIso(yeniOzel.bit), fiyat: parseFloat(yeniOzel.fiyat), paraBirimi: yeniOzel.paraBirimi || 'TRY' });
      setF({ ozelFiyatlar: [...fiyatState.ozelFiyatlar, { id: r.odaTipiOzelFiyatId, bas: r.baslangicTarihi?.slice(0, 10), bit: r.bitisTarihi?.slice(0, 10), fiyat: Number(r.fiyat), paraBirimi: r.paraBirimi || yeniOzel.paraBirimi || 'TRY' }] });
      setYeniOzel({ bas: '', bit: '', fiyat: '', paraBirimi: yeniOzel.paraBirimi || 'TRY' }); setTarihHata('');
    } catch (e) { alert(e?.message || 'Hata oluştu.'); }
    setOzelEkleniyor(false);
  };

  const handleOzelSil = async (id) => {
    try { await apiRequest(`odafiyat/ozel/${id}`, 'DELETE'); setF({ ozelFiyatlar: fiyatState.ozelFiyatlar.filter(f => f.id !== id) }); }
    catch (e) { alert(e?.message || 'Hata oluştu.'); }
  };

  const handleKuralEkle = async () => {
    if (!yeniKural.ad) return;
    try { const r = await apiRequest('cocukfiyatkurallari', 'POST', yeniKural); setCocukKurallari(p => [...p, r]); setYeniKural(bosKural()); setKuralForm(false); }
    catch (e) { alert(e?.message || 'Hata oluştu.'); }
  };

  const handleKuralSil = async (id) => {
    try { await apiRequest(`cocukfiyatkurallari/${id}`, 'DELETE'); setCocukKurallari(p => p.filter(k => k.cocukFiyatKuraliId !== id)); }
    catch (e) { alert(e?.message || 'Hata oluştu.'); }
  };

  if (yukleniyor) return <LoadingSpinner fullPage text="Veriler Hazırlanıyor..." color="rose" />;

  return (
    <div className="p-3 md:p-6 space-y-7 min-h-screen bg-gray-50/50 animate-fadeIn leading-none" style={{ transform: 'translateZ(0)' }}>
      <PageHeader
        icon="💰"
        iconColor="from-rose-500 to-pink-700"
        iconShadow="shadow-rose-200"
        title="Oda Fiyatlandırma"
        subtitle="Günlük ve sezonluk fiyat stratejisi yönetimi"
      >
        <div className="flex items-center gap-4">
          {mesaj && <div className={`px-6 py-3 rounded-2xl text-[10px] font-black uppercase tracking-widest border-2 shadow-sm animate-fade-in ${mesaj.tip === 'OK' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-rose-50 text-rose-600 border-rose-100'}`}>{mesaj.metin}</div>}
          {sekme !== 'cocuk' && (
            <button onClick={handleKaydet} disabled={kaydediliyor || !fiyatState}
              className="bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 disabled:opacity-60 text-white font-black px-10 py-4 rounded-2xl shadow-xl shadow-rose-200 transition-all text-xs uppercase tracking-widest hover:-translate-y-1 transform-gpu leading-none">
              {kaydediliyor ? '⌛ İŞLENİYOR' : '💾 FİYATLARI KAYDET'}
            </button>
          )}
        </div>
      </PageHeader>

      {/* Tipi Seçimi */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 2xl:grid-cols-8 gap-3 leading-none transform-gpu">
        {odaTipleri.map(o => {
          const aktif = seciliId === o.odaTipiId;
          return (
            <button key={o.odaTipiId} onClick={() => setSeciliId(o.odaTipiId)}
              className={`p-5 rounded-[2rem] border-2 transition-all duration-300 transform-gpu leading-none text-left flex flex-col justify-between h-28 relative overflow-hidden group ${aktif ? 'border-rose-500 bg-rose-600 text-white shadow-xl -translate-y-1' : 'border-white bg-white hover:border-rose-100 hover:shadow-lg'}`}>
              <div className={`absolute -right-3 -bottom-3 text-5xl transition-transform duration-500 transform-gpu ${aktif ? 'opacity-20 scale-125 rotate-12' : 'opacity-5 group-hover:opacity-20'}`}>🛌</div>
              <p className={`text-[9px] font-black uppercase tracking-widest leading-none truncate ${aktif ? 'text-rose-200' : 'text-gray-300'}`}>{o.ad}</p>
              <div className="flex items-center justify-between mt-auto">
                <span className={`text-[10px] font-black px-2 py-1 rounded-lg leading-none ${aktif ? 'bg-white/20' : 'bg-gray-50 text-gray-400'}`}>👤 {o.maxKisiSayisi} KİŞİ</span>
                {o.kisiBasiAktif && <span className={`text-[8px] font-black px-2 py-1 rounded-lg leading-none ${aktif ? 'bg-blue-400 shadow-lg shadow-blue-500/50' : 'bg-blue-50 text-blue-400'}`}>KİŞİ BAZLI</span>}
              </div>
            </button>
          );
        })}
      </div>

      {/* Main Panel */}
      {fiyatYukleniyor ? (
        <LoadingSpinner fullPage text="Veriler Hesaplanıyor..." />
      ) : fiyatState && (
        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-scale-in">
          <div className="px-6 md:px-8 py-6 border-b border-gray-100 bg-gray-50/20 flex flex-col lg:flex-row items-center justify-between gap-6">
             <div className="flex items-center gap-5 text-left leading-none">
                <div className="w-14 h-14 bg-rose-50 rounded-2xl flex items-center justify-center text-3xl shadow-inner border border-rose-100 text-rose-500">🏨</div>
                 <div>
                    <h3 className="font-black text-gray-800 text-xl tracking-tighter uppercase leading-none">{aktifTip?.ad}</h3>
                    <div className="flex items-center gap-4 mt-2">
                       <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-none">MAKSİMUM KAPASİTE:</p>
                       <div className="flex items-center gap-2">
                          <button onClick={(e) => handleMaxKisiGuncelle(e, aktifTip, -1)} className="w-8 h-8 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center justify-center text-gray-400 hover:text-rose-500 hover:border-rose-100 transition-all font-black text-lg">-</button>
                          <span className="text-sm font-black text-rose-600 bg-rose-50 px-3 py-1 rounded-xl border border-rose-100 shadow-sm">{aktifTip?.maxKisiSayisi} KİŞİ</span>
                          <button onClick={(e) => handleMaxKisiGuncelle(e, aktifTip, 1)} className="w-8 h-8 rounded-xl bg-white border border-gray-100 shadow-sm flex items-center justify-center text-gray-400 hover:text-rose-500 hover:border-rose-100 transition-all font-black text-lg">+</button>
                       </div>
                    </div>
                 </div>
             </div>
             <div className="flex bg-gray-100/50 p-1.5 rounded-[2rem] gap-1 transform-gpu leading-none overflow-x-auto no-scrollbar max-w-full">
                {[
                  { id: 'standart', label: 'STANDART', icon: '📅', color: 'bg-rose-600' },
                  { id: 'ozel', label: 'ÖZEL DÖNEM', icon: '✨', color: 'bg-amber-500' },
                  { id: 'cocuk', label: 'ÇOCUK POL.', icon: '👶', color: 'bg-emerald-500' }
                ].map(t => (
                  <button key={t.id} onClick={() => setSekme(t.id)}
                    className={`flex-shrink-0 px-6 py-3.5 rounded-[1.5rem] text-[10px] font-black transition-all uppercase tracking-widest flex items-center gap-2 leading-none transform-gpu ${sekme === t.id ? `${t.color} text-white shadow-xl scale-105 shadow-gray-200` : 'text-gray-400 hover:text-gray-600 hover:bg-white'}`}>
                    <span>{t.icon}</span> {t.label}
                  </button>
                ))}
             </div>
          </div>

          <div className="p-6 md:p-10">
            {sekme === 'standart' && (
              <div className="space-y-12 animate-fade-in leading-none text-left">
                <div>
                  <div className="flex items-center justify-between mb-8 ml-2">
                    <h4 className="text-[11px] font-black text-gray-400 uppercase tracking-[0.25em]">📅 HAFTALIK STANDART GECELİK FİYATLAR</h4>
                    <div className="flex items-center gap-2">
                      <span className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Para Birimi:</span>
                      <select value={fiyatState.gunlukParaBirimi || 'TRY'} onChange={e => handleGunlukPbDegistir(e.target.value)}
                        className="bg-white border-2 border-rose-100 rounded-xl px-3 py-1.5 font-black text-xs text-rose-600 focus:outline-none focus:border-rose-400 cursor-pointer shadow-sm">
                        {(paraBirimleri.length > 0 ? paraBirimleri : [{ tcmbKod: 'TRY', ad: 'Türk Lirası' }]).map(pb => (
                          <option key={pb.tcmbKod} value={pb.tcmbKod}>{pb.tcmbKod}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-5 leading-none">
                    {GUNLER.map(g => {
                      const tryEsde = fiyatToTL(fiyatState.gunlukFiyat[g], fiyatState.gunlukParaBirimi, dovizKurlari);
                      return (
                      <div key={g} className={`group rounded-[2rem] p-6 border-2 transition-all duration-300 leading-none ${['Cumartesi', 'Pazar'].includes(g) ? 'bg-rose-50/30 border-rose-100' : 'bg-gray-50/20 border-gray-100 hover:border-gray-200'}`}>
                        <p className={`text-[11px] font-black text-center uppercase tracking-widest mb-4 ${['Cumartesi', 'Pazar'].includes(g) ? 'text-rose-600' : 'text-gray-400'}`}>{g.slice(0, 3)}</p>
                        <div className="relative">
                          <input type="number" value={fiyatState.gunlukFiyat[g]} onChange={e => setF({ gunlukFiyat: { ...fiyatState.gunlukFiyat, [g]: parseFloat(e.target.value) || 0 } })}
                            className="w-full bg-white border-2 border-gray-100 rounded-2xl px-3 py-4 text-lg font-black text-right text-gray-800 transition-all focus:outline-none focus:border-rose-400 shadow-inner h-[58px]" />
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 font-black text-gray-300 text-[9px]">{fiyatState.gunlukParaBirimi || 'TRY'}</span>
                        </div>
                        {tryEsde !== null && <p className="text-[9px] font-black text-emerald-500 text-right mt-1">≈ {tryEsde.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL</p>}
                      </div>
                      );
                    })}
                  </div>
                </div>

                <div className={`rounded-[2.5rem] p-7 md:p-10 border-2 transition-all duration-700 leading-none ${fiyatState.kisiBasiAktif ? 'border-indigo-100 bg-indigo-50/30' : 'border-gray-100 bg-gray-50/10'}`}>
                    <div className="flex items-center justify-between mb-8">
                      <label className="flex items-center gap-5 cursor-pointer leading-none group select-none">
                         <input type="checkbox" checked={fiyatState.kisiBasiAktif} onChange={e => setF({ kisiBasiAktif: e.target.checked })} className="sr-only peer" />
                         <div className="w-16 h-8 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-8 peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-7 after:w-7 after:transition-all peer-checked:bg-indigo-600 relative transform-gpu border border-transparent shadow-inner"></div>
                         <div>
                            <p className={`text-base font-black uppercase tracking-tight leading-none ${fiyatState.kisiBasiAktif ? 'text-indigo-900' : 'text-gray-500'}`}>Kişi Bazlı Fiyatlandırma</p>
                            <p className={`text-[9px] font-black uppercase tracking-widest mt-2 leading-none ${fiyatState.kisiBasiAktif ? 'text-indigo-500 animate-pulse' : 'text-gray-300'}`}>{fiyatState.kisiBasiAktif ? 'Şu an aktif' : 'Devre dışı'}</p>
                         </div>
                      </label>
                      {fiyatState.kisiBasiAktif && (
                        <div className="flex items-center gap-2">
                          <span className="text-[9px] font-black text-indigo-400 uppercase tracking-widest">Para Birimi:</span>
                          <select value={fiyatState.kisiBasiParaBirimi || 'TRY'} onChange={e => handleKisiBasiPbDegistir(e.target.value)}
                            className="bg-white border-2 border-indigo-100 rounded-xl px-3 py-1.5 font-black text-xs text-indigo-600 focus:outline-none focus:border-indigo-400 cursor-pointer shadow-sm">
                            {(paraBirimleri.length > 0 ? paraBirimleri : [{ tcmbKod: 'TRY', ad: 'Türk Lirası' }]).map(pb => (
                              <option key={pb.tcmbKod} value={pb.tcmbKod}>{pb.tcmbKod}</option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>

                    {fiyatState.kisiBasiAktif ? (
                       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 animate-slide-up leading-none">
                          {Array.from({ length: aktifTip?.maxKisiSayisi || 2 }, (_, i) => {
                             const k = `${i + 1}.Kişi`;
                             const tryEsde = fiyatToTL(fiyatState.kisiBasiFiyat[k], fiyatState.kisiBasiParaBirimi, dovizKurlari);
                             return (
                               <div key={k} className="bg-white rounded-3xl border-2 border-indigo-100 p-6 transition-all hover:bg-indigo-50 text-center shadow-lg shadow-indigo-100/30">
                                  <p className="text-[10px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-4">{k}</p>
                                  <div className="relative">
                                    <input type="number" value={fiyatState.kisiBasiFiyat[k] || 0} onChange={e => setF({ kisiBasiFiyat: { ...fiyatState.kisiBasiFiyat, [k]: parseFloat(e.target.value) || 0 } })}
                                      className="w-full bg-indigo-50/50 border-2 border-indigo-100 rounded-2xl px-3 py-4 text-lg font-black text-right text-indigo-900 focus:outline-none focus:border-indigo-400" />
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 font-black text-indigo-300 text-[9px]">{fiyatState.kisiBasiParaBirimi || 'TRY'}</span>
                                  </div>
                                  {tryEsde !== null && <p className="text-[9px] font-black text-emerald-500 text-right mt-1">≈ {tryEsde.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL</p>}
                               </div>
                             );
                          })}
                       </div>
                    ) : (
                      <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest text-center py-5 italic border-2 border-dashed border-gray-100 rounded-[1.5rem]">Kişi bazlı fiyat kapalıyken sadece günlük/gecelik fiyatlar geçerlidir.</p>
                    )}
                </div>
              </div>
            )}

            {sekme === 'ozel' && (
              <div className="space-y-10 animate-fade-in leading-none text-left">
                 <div className="bg-amber-50/50 rounded-[2rem] border-2 border-amber-100 p-8 space-y-8 leading-none">
                    <h4 className="text-[10px] font-black text-amber-600 uppercase tracking-[0.25em] leading-none mb-4">✨ YENİ ÖZEL DÖNEM FİYATI TANIMLA</h4>
                    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 items-end leading-none">
                       <div>
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">BAŞLANGIÇ</label>
                          <DatePicker value={yeniOzel.bas} onChange={v => { setYeniOzel(p => ({ ...p, bas: v, bit: '' })); setTarihHata(''); }} />
                       </div>
                       <div>
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">BİTİŞ</label>
                          <DatePicker value={yeniOzel.bit} minDate={yeniOzel.bas} onChange={v => { setYeniOzel(p => ({ ...p, bit: v })); setTarihHata(''); }} />
                       </div>
                       <div>
                          <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">GECELİK FİYAT</label>
                          <div className="flex gap-2">
                            <div className="relative leading-none flex-1">
                              <input type="number" value={yeniOzel.fiyat} onChange={e => setYeniOzel(p => ({ ...p, fiyat: e.target.value }))} className="w-full bg-white border-2 border-amber-100 rounded-2xl px-5 py-3.5 font-black text-lg text-amber-700 focus:outline-none focus:border-amber-400 h-[58px] text-right pr-16 shadow-inner" />
                              <span className="absolute right-3 top-1/2 -translate-y-1/2 font-black text-amber-300 text-[10px] leading-none"></span>
                            </div>
                            <select value={yeniOzel.paraBirimi || 'TRY'} onChange={e => setYeniOzel(p => ({ ...p, paraBirimi: e.target.value }))}
                              className="bg-white border-2 border-amber-100 rounded-2xl px-3 font-black text-xs text-amber-700 focus:outline-none focus:border-amber-400 h-[58px] cursor-pointer">
                              {(paraBirimleri.length > 0 ? paraBirimleri : [{ tcmbKod: 'TL', ad: 'TL' }]).map(pb => (
                                <option key={pb.tcmbKod} value={pb.tcmbKod}>{pb.tcmbKod}</option>
                              ))}
                            </select>
                          </div>
                          {yeniOzel.fiyat && yeniOzel.paraBirimi && yeniOzel.paraBirimi !== 'TL' && yeniOzel.paraBirimi !== 'TRY' && (() => { const t = fiyatToTL(parseFloat(yeniOzel.fiyat), yeniOzel.paraBirimi, dovizKurlari); return t ? <p className="text-[9px] font-black text-emerald-500 mt-1">≈ {t.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL</p> : null; })()}
                       </div>
                       <button onClick={handleOzelEkle} disabled={ozelEkleniyor || !yeniOzel.bas || !yeniOzel.bit || !yeniOzel.fiyat} 
                         className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-black px-10 py-5 rounded-2xl text-[10px] uppercase tracking-widest shadow-xl shadow-amber-200 transition-all transform-gpu active:scale-95 h-[58px] leading-none">
                         {ozelEkleniyor ? '...' : 'DÖNEMİ EKLE'}
                       </button>
                    </div>
                    {tarihHata && <p className="text-[10px] font-black text-rose-500 animate-shake uppercase tracking-widest px-5 mt-2">⚠️ {tarihHata}</p>}
                 </div>

                 <div className="bg-white border-2 border-gray-100 rounded-[2.5rem] overflow-hidden leading-none shadow-sm">
                    <DataTable
                       anahtarAlan="id"
                       satirlar={fiyatState.ozelFiyatlar}
                       bosIkon="📅"
                       bosMetin="Aktif dönem bulunamadı"
                       kolonlar={[
                         {
                           anahtar: 'bas', baslik: 'Dönem Tarih Aralığı', siralanir: true,
                           ciz: f => (
                             <div className="flex items-center gap-4">
                               <span className="w-2 h-2 bg-amber-400 rounded-full" />
                               <span className="text-sm font-black text-gray-700">{tarihGoster(f.bas)}</span>
                               <span className="text-amber-300">➔</span>
                               <span className="text-sm font-black text-gray-700">{tarihGoster(f.bit)}</span>
                             </div>
                           ),
                         },
                         {
                           anahtar: 'fiyat', baslik: 'Gecelik Fiyat', hizala: 'sag', genislik: 'w-48', siralanir: true,
                           ciz: f => {
                             const tl = (f.paraBirimi && f.paraBirimi !== 'TL' && f.paraBirimi !== 'TRY')
                               ? fiyatToTL(f.fiyat, f.paraBirimi, dovizKurlari) : null;
                             return (
                               <span className="font-black text-amber-600 text-xl">
                                 {fmt(f.fiyat, f.paraBirimi)}
                                 {tl ? (
                                   <span className="block text-[9px] text-emerald-500 font-black">
                                     ≈ {tl.toLocaleString('tr-TR', { maximumFractionDigits: 0 })} TL
                                   </span>
                                 ) : null}
                               </span>
                             );
                           },
                         },
                         {
                           anahtar: 'islem', baslik: '', hizala: 'sag', genislik: 'w-24',
                           ciz: f => <DeleteButton onClick={() => handleOzelSil(f.id)} />,
                         },
                       ]}
                    />
                 </div>
              </div>
            )}

            {sekme === 'cocuk' && (
              <div className="space-y-10 animate-fade-in leading-none text-left">
                 <div className="bg-emerald-50/50 rounded-[2rem] border-2 border-emerald-100 p-8 space-y-8 leading-none">
                    {!kuralForm ? (
                       <button onClick={() => setKuralForm(true)} className="bg-emerald-600 text-white font-black px-12 py-5 rounded-[1.8rem] text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-emerald-100 transition-all hover:-translate-y-1 active:scale-95 leading-none">＋ YENİ ÇOCUK POLİTİKASI EKLE</button>
                    ) : (
                       <div className="space-y-4 animate-slide-up leading-none text-left">
                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                          <div>
                             <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">KURAL ADI</label>
                             <input value={yeniKural.ad} onChange={e => setYeniKural(p => ({ ...p, ad: e.target.value.toUpperCase() }))} className="w-full bg-white border-2 border-emerald-100 rounded-2xl px-5 py-3.5 font-black text-xs h-[58px] uppercase focus:outline-none focus:border-emerald-500" placeholder="ÖRN: 0-6 YAŞ" />
                          </div>
                          <div className="flex gap-2">
                             <div className="flex-1">
                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">MİN YAŞ</label>
                                <input type="number" value={yeniKural.minYas} onChange={e => setYeniKural(p => ({ ...p, minYas: parseInt(e.target.value) || 0 }))} className="w-full bg-white border-2 border-emerald-100 rounded-2xl px-5 py-3.5 font-black h-[58px] text-center" />
                             </div>
                             <div className="flex-1">
                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">MAKS YAŞ</label>
                                <input type="number" value={yeniKural.maxYas} onChange={e => setYeniKural(p => ({ ...p, maxYas: parseInt(e.target.value) || 0 }))} className="w-full bg-white border-2 border-emerald-100 rounded-2xl px-5 py-3.5 font-black h-[58px] text-center" />
                             </div>
                          </div>
                          <div>
                             <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">TÜR</label>
                             <select value={yeniKural.fiyatTipi} onChange={e => setYeniKural(p => ({ ...p, fiyatTipi: e.target.value }))} className="w-full bg-white border-2 border-emerald-100 rounded-2xl px-5 py-3.5 font-black h-[58px] cursor-pointer text-xs uppercase">
                                <option value="UCRETSIZ">BEDELSİZ</option>
                                <option value="YUZDE">YÜZDE (%)</option>
                                <option value="SABIT">SABİT TUTAR</option>
                             </select>
                          </div>
                          {yeniKural.fiyatTipi !== 'UCRETSIZ' && (
                            <div className="flex gap-2">
                              <div className="flex-1 relative">
                                <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">DEĞER</label>
                                <input type="number" value={yeniKural.deger} onChange={e => setYeniKural(p => ({ ...p, deger: parseFloat(e.target.value) || 0 }))}
                                  className="w-full bg-white border-2 border-emerald-100 rounded-2xl px-5 py-3.5 font-black h-[58px] text-right text-lg text-emerald-700 focus:outline-none focus:border-emerald-500 shadow-inner"
                                  style={{ paddingRight: yeniKural.fiyatTipi === 'YUZDE' ? '2.5rem' : '1.25rem' }} />
                                {yeniKural.fiyatTipi === 'YUZDE' && <span className="absolute right-3 top-[calc(50%+10px)] -translate-y-1/2 font-black text-emerald-300 text-sm">%</span>}
                              </div>
                              {yeniKural.fiyatTipi === 'SABIT' && (
                                <div className="flex-shrink-0">
                                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest block mb-1.5 ml-1">BİRİM</label>
                                  <select value={yeniKural.paraBirimi || 'TRY'} onChange={e => setYeniKural(p => ({ ...p, paraBirimi: e.target.value }))}
                                    className="bg-white border-2 border-emerald-100 rounded-2xl px-3 font-black h-[58px] cursor-pointer text-xs text-emerald-700 focus:outline-none focus:border-emerald-500 w-20">
                                    {(paraBirimleri.length > 0 ? paraBirimleri : [{ tcmbKod: 'TRY', ad: 'TRY' }]).map(pb => (
                                      <option key={pb.tcmbKod} value={pb.tcmbKod}>{pb.tcmbKod}</option>
                                    ))}
                                  </select>
                                </div>
                              )}
                            </div>
                          )}
                         </div>
                         <div className="flex gap-3 justify-end">
                           <button onClick={handleKuralEkle} disabled={!yeniKural.ad} className="bg-emerald-600 disabled:opacity-50 text-white font-black px-10 h-[50px] rounded-2xl text-[10px] uppercase tracking-widest shadow-lg shadow-emerald-100 transition-all hover:scale-[1.03] active:scale-95 leading-none">EKLE</button>
                           <button onClick={() => setKuralForm(false)} className="w-[50px] h-[50px] bg-white text-gray-400 rounded-2xl flex items-center justify-center border-2 border-emerald-100 hover:bg-rose-50 hover:text-rose-500 transition-all leading-none">✕</button>
                         </div>
                       </div>
                    )}
                 </div>

                 <div className="bg-white border-2 border-gray-100 rounded-[2.5rem] overflow-hidden leading-none shadow-sm">
                    <DataTable
                       anahtarAlan="cocukFiyatKuraliId"
                       satirlar={cocukKurallari}
                       bosIkon="🧒"
                       bosMetin="Tanımlı kural bulunamadı"
                       kolonlar={[
                         {
                           anahtar: 'ad', baslik: 'Kural Tanımı',
                           ciz: k => <span className="font-black text-gray-700 uppercase tracking-tight">{k.ad}</span>,
                         },
                         {
                           anahtar: 'yas', baslik: 'Yaş Aralığı', hizala: 'orta', genislik: 'w-40',
                           deger: k => k.minYas,
                           ciz: k => (
                             <span className="bg-gray-100 text-gray-500 font-black text-[9px] px-3 py-1.5 rounded-lg border border-gray-200">
                               {k.minYas} - {k.maxYas} YAŞ
                             </span>
                           ),
                         },
                         {
                           anahtar: 'deger', baslik: 'Extra Bedel', hizala: 'orta', genislik: 'w-40',
                           ciz: k => (
                             <span className={'font-black text-sm px-4 py-2 rounded-xl inline-block border ' +
                               (k.fiyatTipi === 'UCRETSIZ'
                                 ? 'bg-emerald-50 text-emerald-600 border-emerald-100'
                                 : 'bg-blue-50 text-blue-600 border-blue-100')}>
                               {k.fiyatTipi === 'UCRETSIZ' ? 'BEDELSİZ'
                                 : k.fiyatTipi === 'YUZDE' ? `%${k.deger}`
                                 : `${k.deger} ${k.paraBirimi || 'TRY'}`}
                             </span>
                           ),
                         },
                         {
                           anahtar: 'islem', baslik: '', hizala: 'sag', genislik: 'w-24',
                           ciz: k => (
                             <DeleteButton onClick={() => { if (window.confirm('Emin misiniz?')) handleKuralSil(k.cocukFiyatKuraliId); }} />
                           ),
                         },
                       ]}
                    />
                 </div>
              </div>
            )}
          </div>
          
          {sekme !== 'cocuk' && (
            <div className="px-8 py-10 bg-gray-50/50 border-t border-gray-100 flex flex-col items-center">
               <button onClick={handleKaydet} disabled={kaydediliyor} 
                 className="w-full max-w-4xl py-6 bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white rounded-[2rem] font-black text-sm md:text-base uppercase tracking-[0.3em] shadow-2xl shadow-rose-200 transition-all hover:scale-[1.01] active:translate-y-1 transform-gpu leading-none mb-4 flex items-center justify-center gap-4 group/save">
                 <span className="group-hover/save:scale-125 transition-transform duration-500">🚀</span>
                 {kaydediliyor ? 'GÜNCELLENİYOR...' : 'FİYATLARI KAYDET VE SİSTEMİ GÜNCELLE'}
               </button>
               <p className="text-[10px] font-black text-gray-300 uppercase tracking-widest mt-2 animate-pulse leading-none italic">Sistemdeki tüm kanallar bu fiyatlar üzerinden işlem yapacaktır.</p>
            </div>
          )}
        </div>
      )}

      <UsageInfo
        color="rose"
        description="Oda Fiyatlandırma modülü, her oda tipi için haftalık fiyatları, kişi sayısı bazlı fiyatlandırmayı ve özel dönem fiyatlarını yönetmenizi sağlar."
        scenarios={[
          { title: "Haftalık Esnek Fiyat", content: "Hafta içi ve hafta sonu (Cuma/Cumartesi) için farklı fiyatlar belirleyerek gelirinizi optimize edebilirsiniz." },
          { title: "Kişi Sayısına Göre Ücret", content: "Tek kişi veya ek misafirler için farklı ücretlendirmeler tanımlayabilirsiniz." },
          { title: "Sezonluk Geçişler", content: "Bayram tatillerini veya festivalleri 'Özel Dönem' olarak tanımlayarak standart fiyatları devre dışı bırakabilirsiniz." }
        ]}
      />

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.4s ease-out forwards; }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.98); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scaleIn 0.3s ease-out forwards; }
        @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-5px); } 75% { transform: translateX(5px); } }
        .animate-shake { animation: shake 0.3s ease-in-out infinite; animation-iteration-count: 2; }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        .animate-slide-up { animation: slideUp 0.4s ease-out forwards; }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}
